import {useRef, useCallback} from 'react'
import {UserFeedback, type RatingOption, type FeedbackRef} from '@github-ui/user-feedback'
import {type DialogRef, InterviewSurveyDialog} from '@github-ui/copilot-chat/components/InterviewSurveyDialog'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'

interface FeedbackDialogProps<Rating> {
  surveyDialogRef: React.RefObject<DialogRef | null>
  feedbackDialogRef: React.RefObject<FeedbackRef<Rating> | null>
  initialRatingRef: React.RefObject<Rating | undefined>
  ratingOptions: Array<RatingOption<Rating>>
  source: string
  showSurvey: boolean
  returnFocusRef?: React.RefObject<HTMLElement | null>
  onClose?: (reason: 'submit' | 'cancel' | 'dismiss') => void
  onSubmitted?: (rating: Rating) => void
}

function FeedbackDialog<Rating>({
  surveyDialogRef,
  feedbackDialogRef,
  initialRatingRef,
  ratingOptions,
  source,
  showSurvey,
  returnFocusRef,
  onClose,
  onSubmitted,
}: FeedbackDialogProps<Rating>) {
  const onSubmit = useCallback(
    async (rating: Rating | null, text: string): Promise<string[]> => {
      const errors = []

      if (rating == null) {
        errors.push('Please select a rating.')
      }

      const limit = 2000
      if (text.length > limit) {
        errors.push(`Please keep your feedback within ${limit} characters or less.`)
      }

      if (errors.length > 0) {
        return errors
      }

      const body = {
        subject: `mission-control:${source}`,
        rating,
        content: text,
        hostname: window.location.hostname,
        path: window.location.pathname,
        mode: 'immersive',
      }

      try {
        const response = await verifiedFetchJSON('/github-copilot/feedback', {method: 'POST', body})
        if (!response.ok) {
          errors.push('An error occurred while submitting your feedback.')
        } else if (rating != null) {
          // Notify that feedback was successfully submitted
          onSubmitted?.(rating)
        }
      } catch {
        errors.push('An error occurred while submitting your feedback.')
      }

      return errors
    },
    [source, onSubmitted],
  )

  const onSurveyClose = useCallback(
    (reason: 'close' | 'no-thanks') => {
      if (reason === 'no-thanks') {
        feedbackDialogRef.current?.openDialog(initialRatingRef.current)
      } else {
        // User closed the survey dialog - submit with initial rating if available
        if (initialRatingRef.current != null) {
          void onSubmit(initialRatingRef.current, '')
        }
        onClose?.('dismiss')
      }
    },
    [feedbackDialogRef, initialRatingRef, onSubmit, onClose],
  )

  const onFeedbackClose = useCallback(
    (reason: 'submit' | 'cancel' | 'dismiss') => {
      if (reason === 'dismiss' && initialRatingRef.current) {
        // User dismissed via X or Escape - auto-submit with initial rating if available
        void onSubmit(initialRatingRef.current, '')
      }
      onClose?.(reason)
    },
    [initialRatingRef, onSubmit, onClose],
  )

  return (
    <>
      {showSurvey && <InterviewSurveyDialog ref={surveyDialogRef} onClose={onSurveyClose} />}
      <UserFeedback
        ref={feedbackDialogRef}
        options={ratingOptions}
        onSubmit={onSubmit}
        onClose={onFeedbackClose}
        returnFocusRef={returnFocusRef}
      />
    </>
  )
}

export function useFeedbackDialog<Rating>(source: string, ratingOptions: Array<RatingOption<Rating>>) {
  const surveyDialogRef = useRef<DialogRef>(null)
  const feedbackDialogRef = useRef<FeedbackRef<Rating>>(null)
  const initialRatingRef = useRef<Rating | undefined>(undefined)

  const showSurvey = isFeatureEnabled('copilot_mission_control_interview_survey')

  const openFeedback = useCallback(
    (initialRatingValue?: Rating) => {
      initialRatingRef.current = initialRatingValue
      if (showSurvey) {
        surveyDialogRef.current?.openDialog()
      } else {
        feedbackDialogRef.current?.openDialog(initialRatingValue)
      }
    },
    [showSurvey],
  )

  const DialogComponent = useCallback(
    (props: Pick<FeedbackDialogProps<Rating>, 'onClose' | 'onSubmitted' | 'returnFocusRef'>) => (
      <FeedbackDialog<Rating>
        surveyDialogRef={surveyDialogRef}
        feedbackDialogRef={feedbackDialogRef}
        initialRatingRef={initialRatingRef}
        ratingOptions={ratingOptions}
        source={source}
        showSurvey={showSurvey}
        {...props}
      />
    ),
    [source, ratingOptions, showSurvey],
  )

  return {
    openFeedback,
    FeedbackDialog: DialogComponent,
  }
}
